import React from "react";
import { X, Plus, Save } from "lucide-react";
import Mo3tiButton from "../../layout/Mo3tiButton";
import ReusableDropdown from "../../../../../components/ReusableDropdown";

// Mirrors the backend isPreTaxDeduction helper in payrollService.js
const isPreTaxDeductionType = (type) => {
  if (!type) return null; // null = not selected yet
  const t = type.toLowerCase();
  return t === "absence" || t === "sick_leave" || t === "sick_leave_25" || t === "late_arrival";
};

const AddDeductionForm = ({
  t,
  theme,
  formStyles,
  showAddForm,
  setShowAddForm,
  newDeduction,
  setNewDeduction,
  lateCalcLoading,
  fetchLateDeduction,
  handleLateHoursChange,
  handleAddDeduction,
  loading,
  language,
  editingDeductionId,
}) => {
  const isEditing = !!editingDeductionId;
  return (
    <>
      {/* Add Deduction Form */}
      <div style={formStyles.section}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <h3
            style={{
              margin: 0,
              color: theme === "dark" ? "#ffffff" : "#1a1a1a",
              fontSize: "20px",
              fontWeight: "700",
            }}
          >
            {isEditing ? t("editDeduction") || "Edit Deduction" : t("addDeduction")}
          </h3>
          <Mo3tiButton
            variant="secondary"
            icon={showAddForm ? X : Plus}
            onClick={() => setShowAddForm(!showAddForm)}
          >
            {showAddForm ? t("cancel") : (isEditing ? t("editDeduction") || "Edit Deduction" : t("addDeduction"))}
          </Mo3tiButton>
        </div>

        {showAddForm && (
          <div>
            {/* Deduction Treatment Selector & Alert Banner */}
            <div style={{ ...formStyles.formGroup, marginBottom: "20px" }}>
              <label style={formStyles.label}>
                {t("deductionTreatment") || "Deduction Treatment"}
              </label>
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  marginTop: "6px",
                  marginBottom: "12px",
                }}
              >
                <button
                  type="button"
                  onClick={() =>
                    setNewDeduction((prev) => ({ ...prev, isPreTax: true }))
                  }
                  style={{
                    flex: 1,
                    padding: "12px 18px",
                    borderRadius: "12px",
                    fontWeight: "700",
                    fontSize: "14px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    border: newDeduction.isPreTax
                      ? "2px solid #8b5cf6"
                      : `2px solid ${theme === "dark" ? "rgba(255,255,255,0.15)" : "#e2e8f0"}`,
                    background: newDeduction.isPreTax
                      ? (theme === "dark" ? "rgba(139, 92, 246, 0.25)" : "#f3e8ff")
                      : (theme === "dark" ? "rgba(255,255,255,0.03)" : "#f8fafc"),
                    color: newDeduction.isPreTax
                      ? (theme === "dark" ? "#c084fc" : "#6d28d9")
                      : (theme === "dark" ? "#94a3b8" : "#64748b"),
                    boxShadow: newDeduction.isPreTax
                      ? "0 4px 12px rgba(139, 92, 246, 0.15)"
                      : "none",
                  }}
                >
                  {t("gross") || "Gross"}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setNewDeduction((prev) => ({ ...prev, isPreTax: false }))
                  }
                  style={{
                    flex: 1,
                    padding: "12px 18px",
                    borderRadius: "12px",
                    fontWeight: "700",
                    fontSize: "14px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    border: !newDeduction.isPreTax
                      ? "2px solid #10b981"
                      : `2px solid ${theme === "dark" ? "rgba(255,255,255,0.15)" : "#e2e8f0"}`,
                    background: !newDeduction.isPreTax
                      ? (theme === "dark" ? "rgba(16, 185, 129, 0.25)" : "#ecfdf5")
                      : (theme === "dark" ? "rgba(255,255,255,0.03)" : "#f8fafc"),
                    color: !newDeduction.isPreTax
                      ? (theme === "dark" ? "#34d399" : "#047857")
                      : (theme === "dark" ? "#94a3b8" : "#64748b"),
                    boxShadow: !newDeduction.isPreTax
                      ? "0 4px 12px rgba(16, 185, 129, 0.15)"
                      : "none",
                  }}
                >
                  {t("net") || "Net"}
                </button>
              </div>

              <div
                style={{
                  padding: "12px 16px",
                  borderRadius: "10px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  background: newDeduction.isPreTax
                    ? (theme === "dark" ? "rgba(139, 92, 246, 0.1)" : "#f5f3ff")
                    : (theme === "dark" ? "rgba(16, 185, 129, 0.1)" : "#ecfdf5"),
                  borderLeft: `4px solid ${newDeduction.isPreTax ? "#8b5cf6" : "#10b981"}`,
                }}
              >
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: "700",
                    color: newDeduction.isPreTax
                      ? (theme === "dark" ? "#c084fc" : "#6d28d9")
                      : (theme === "dark" ? "#34d399" : "#047857"),
                  }}
                >
                  {newDeduction.isPreTax
                    ? (t("gross") || "Gross")
                    : (t("net") || "Net")}
                </span>
                <span style={{ fontSize: "12px", color: theme === "dark" ? "#cbd5e1" : "#475569" }}>
                  {newDeduction.isPreTax
                    ? (t("grossDeductionDesc") ||
                      "This deduction will reduce the taxable gross amount.")
                    : (t("netDeductionDesc") || "This deduction will be taken from the final net amount.")}
                </span>
                {newDeduction.isPreTax && (
                  <span
                    style={{
                      fontSize: "12px",
                      color: theme === "dark" ? "#cbd5e1" : "#475569",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "6px",
                      marginTop: "2px",
                    }}
                  >
                    <span style={{ fontWeight: 700 }}>ℹ</span>
                    <span>
                      {t("grossDeductionTaxShieldNote") ||
                        "The amount you enter here is the Gross figure. Once saved, the system converts it to the employee's real net-pay impact (usually a smaller number) based on their tax bracket and insurance — some of what's removed from Gross would have gone to tax anyway, so the employee's take-home pay drops by less than the amount you type. You'll see the actual applied amount in the deductions list right after saving."}
                    </span>
                  </span>
                )}
              </div>
            </div>

            <div style={formStyles.formGrid}>
              <div style={formStyles.formGroup}>
                <label style={formStyles.label}>{t("deductionType")}</label>
                <ReusableDropdown
                  items={[
                    { id: "absence", label: t("absence") },
                    { id: "penalty", label: t("penalty") },
                    { id: "advance", label: t("advance") },
                    { id: "insurance", label: t("insurance") },
                    { id: "sick_leave_25", label: t("sickLeave") },
                    { id: "late_arrival", label: t("lateArrival") },
                    // ── The rest of the types the bulk-import template and
                    // backend already recognize (see exportAllowanceDeductionTemplate
                    // in payrollController.js) — added so every type that can
                    // be imported can also be added here one at a time.
                    { id: "late", label: t("late") || "Late" },
                    { id: "unpaid_leave", label: t("unpaidLeave") || "Unpaid Leave" },
                    { id: "aman_card", label: t("amanCard") || "Aman Card" },
                    { id: "amen_gebak", label: t("amenGebak") || "Amen Gebak" },
                    { id: "premium_card", label: t("premiumCard") || "Premium Card" },
                    { id: "computer_installment", label: t("computerInstallment") || "Computer Installment" },
                    { id: "raya_installment", label: t("rayaInstallment") || "Raya Installment" },
                    { id: "salary_advance", label: t("salaryAdvance") || "Salary Advance" },
                    { id: "emergency_fund", label: t("emergencyFund") || "Emergency Fund" },
                    { id: "medical_insurance", label: t("medicalInsurance") || "Medical Insurance" },
                    { id: "adjustment", label: t("adjustment") || "Adjustment" },
                    { id: "other", label: t("other") },
                  ]}
                  value={newDeduction.type || ""}
                  onChange={(val) => {
                    if (val) {
                      setNewDeduction({
                        ...newDeduction,
                        type: val,
                      });
                      if (val === "late_arrival") {
                        fetchLateDeduction();
                      }
                    }
                  }}
                  onSelect={(item) => {
                    setNewDeduction({ ...newDeduction, type: item.id });
                    if (item.id === "late_arrival") {
                      fetchLateDeduction();
                    }
                  }}
                  placeholder={t("selectType")}
                  theme={theme}
                  language={language}
                  maxItems={30}
                  getItemKey={(item) => item.id}
                  getItemLabel={(item) => item.label}
                  showAllOnArrowClick={true}
                  showClear={true}
                  onClear={() =>
                    setNewDeduction({
                      ...newDeduction,
                      type: "",
                      amount: "",
                      description: "",
                    })
                  }
                  inputStyle={formStyles.input}
                />
              </div>

              <div style={formStyles.formGroup}>
                <label style={formStyles.label}>{t("amount")}</label>
                {lateCalcLoading && newDeduction.type === "late_arrival" ? (
                  <div
                    style={{
                      ...formStyles.input,
                      display: "flex",
                      alignItems: "center",
                      color: theme === "dark" ? "#aaa" : "#888",
                    }}
                  >
                    {t("calculatingLate")}
                  </div>
                ) : (
                  <input
                    type="number"
                    style={{
                      ...formStyles.input,
                      ...(newDeduction.type === "late_arrival"
                        ? {
                          backgroundColor: theme === "dark" ? "#333" : "#f0f0f0",
                          cursor: "not-allowed",
                        }
                        : {}),
                    }}
                    value={newDeduction.amount}
                    onChange={(e) => {
                      if (newDeduction.type !== "late_arrival") {
                        setNewDeduction({
                          ...newDeduction,
                          amount: e.target.value,
                        });
                      }
                    }}
                    readOnly={newDeduction.type === "late_arrival"}
                    placeholder="0.00"
                  />
                )}
              </div>

              <div style={formStyles.formGroup}>
                <label style={formStyles.label}>
                  {newDeduction.type === "late_arrival"
                    ? t("lateHours")
                    : t("days")}
                </label>
                <input
                  type="number"
                  style={formStyles.input}
                  value={newDeduction.days}
                  onChange={(e) => {
                    if (newDeduction.type === "late_arrival") {
                      handleLateHoursChange(e.target.value);
                    } else {
                      setNewDeduction({
                        ...newDeduction,
                        days: e.target.value,
                      });
                    }
                  }}
                  placeholder={newDeduction.type === "late_arrival" ? "0" : "0"}
                  step={newDeduction.type === "late_arrival" ? "0.5" : "1"}
                />
              </div>
            </div>



            <div style={formStyles.formGroup}>
              <label style={formStyles.label}>{t("description")}</label>
              <textarea
                style={{
                  ...formStyles.input,
                  minHeight: "80px",
                  resize: "vertical",
                }}
                value={newDeduction.description}
                onChange={(e) =>
                  setNewDeduction({
                    ...newDeduction,
                    description: e.target.value,
                  })
                }
                placeholder={t("descriptionPlaceholder")}
              />
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                marginTop: "20px",
              }}
            >
              <Mo3tiButton
                variant="secondary"
                icon={Save}
                onClick={handleAddDeduction}
                disabled={loading}
              >
                {loading ? t("saving") : (isEditing ? t("update") || "Update" : t("saveDeduction"))}
              </Mo3tiButton>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AddDeductionForm;